import { useEffect, useRef } from 'react';
import { usePdfStore } from '@/stores/pdfStore';
import { renderThumbnailDataUrl } from '@/lib/pdf/pdfDocument';

/** Horizontal page chips for phone bottom chrome (replaces left thumbs rail). */
export default function PdfMobilePagesStrip() {
  const pdfProxy = usePdfStore((s) => s.pdfProxy);
  const pageCount = usePdfStore((s) => s.pageCount);
  const activePageIndex = usePdfStore((s) => s.activePageIndex);
  const thumbUrls = usePdfStore((s) => s.thumbUrls);
  const setActivePage = usePdfStore((s) => s.setActivePage);
  const setThumbUrl = usePdfStore((s) => s.setThumbUrl);
  const loadingRef = useRef<Set<number>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pdfProxy || pageCount === 0) return;
    let cancelled = false;

    const loadNearby = async () => {
      const order = [
        activePageIndex,
        activePageIndex - 1,
        activePageIndex + 1,
        ...Array.from({ length: pageCount }, (_, i) => i),
      ];
      const seen = new Set<number>();

      for (const idx of order) {
        if (cancelled) break;
        if (idx < 0 || idx >= pageCount || seen.has(idx)) continue;
        seen.add(idx);
        if (thumbUrls[idx] || loadingRef.current.has(idx)) continue;
        loadingRef.current.add(idx);
        try {
          const url = await renderThumbnailDataUrl(pdfProxy, idx + 1);
          if (!cancelled) setThumbUrl(idx, url);
        } catch (err) {
          console.warn('Thumb render failed', idx, err);
        } finally {
          loadingRef.current.delete(idx);
        }
      }
    };

    void loadNearby();
    return () => {
      cancelled = true;
    };
  }, [pdfProxy, pageCount, activePageIndex, thumbUrls, setThumbUrl]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const chip = root.querySelector<HTMLElement>(
      `[data-page-index="${activePageIndex}"]`
    );
    chip?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [activePageIndex]);

  if (!pageCount) return null;

  return (
    <div className="pdfe-mobile-pages" aria-label="Pages">
      <span className="pdfe-mobile-pages-label">Pages</span>
      <div className="pdfe-mobile-pages-scroll" ref={scrollRef}>
        {Array.from({ length: pageCount }, (_, i) => {
          const active = i === activePageIndex;
          const thumb = thumbUrls[i];
          return (
            <button
              key={i}
              type="button"
              data-page-index={i}
              className={`pdfe-mobile-page-chip${active ? ' is-active' : ''}${
                thumb ? ' has-thumb' : ''
              }`}
              onClick={() => setActivePage(i)}
              aria-label={`Page ${i + 1}`}
              aria-current={active ? 'page' : undefined}
            >
              {thumb ? (
                <img className="pdfe-mobile-page-thumb" src={thumb} alt="" draggable={false} />
              ) : (
                <span className="pdfe-mobile-page-num">{i + 1}</span>
              )}
            </button>
          );
        })}
      </div>
      <span className="pdfe-mobile-pages-count">
        {activePageIndex + 1} / {pageCount}
      </span>
    </div>
  );
}
